import type { JSX } from "preact";
import { useCallback, useEffect, useState } from "preact/hooks";

import { type Session, api, can, loadSession, saveSession } from "./api";
import { AccountView } from "./views/Account";
import { ConversationsView } from "./views/Conversations";
import { KnowledgeView } from "./views/Knowledge";
import { ModelsView } from "./views/Models";
import { NotificationsView, type UnreadCount } from "./views/Notifications";
import { Login } from "./views/Login";
import { OverviewView } from "./views/Overview";
import { ReferralsView } from "./views/Referrals";
import { ReviewView } from "./views/Review";
import { RulesView } from "./views/Rules";
import { SettingsView } from "./views/Settings";
import { UsersView } from "./views/Users";

/**
 * Admin felület.
 *
 * Hash-alapú útvonalválasztás, router-könyvtár nélkül. Ez a felület hat
 * nézetből áll, egymásba ágyazás nélkül — egy router több függőséget és
 * több fogalmat hozna, mint amennyit megoldana.
 */

interface Route {
  id: string;
  label: string;
  hint: string;
  permission?: string;
}

const ROUTES: Route[] = [
  { id: "attekintes", label: "Áttekintés", hint: "Mi történt, mibe került" },
  { id: "ertesitesek", label: "Értesítések", hint: "Hibák és fontos események" },
  { id: "review", label: "Teendők", hint: "Amit a rendszer nem vállalt egyedül" },
  { id: "beutalok", label: "Feldolgozások", hint: "Teljes nyomvonal" },
  {
    id: "beszelgetesek",
    label: "Beszélgetések",
    hint: "Mit kérdeztek a chattől",
    // Egészségügyi adatot tartalmazhat: csak adminisztrátornak.
    permission: "beszelgetest_olvas",
  },
  { id: "kifejezesek", label: "Kifejezések", hint: "Fel nem ismert megnevezések" },
  { id: "szabalyok", label: "Szabályok", hint: "Árlista-verziók" },
  { id: "tudasbazis", label: "Tudásbázis", hint: "Dokumentumok és sablonok" },
  { id: "beallitasok", label: "Beállítások", hint: "Kapcsolatok és küszöbök" },
  {
    id: "modellek",
    label: "Modellek",
    hint: "Modellválasztás és próbapad",
    // A modellcsere a költséget és a pontosságot is érinti; a Próbapad fizetős.
    permission: "beallit",
  },
  {
    id: "felhasznalok",
    label: "Felhasználók",
    hint: "Ki léphet be, és mit tehet",
    permission: "felhasznalot_kezel",
  },
  { id: "fiokom", label: "Fiókom", hint: "Saját jelszó" },
];

/**
 * A menü a JOGOKAT követi.
 *
 * A `permission` mező eddig ott volt, de nem szűrt semmit: minden nézet
 * minden szerepkörnek látszott. A szerver ugyan elutasította volna a
 * kérést, de a felhasználó addigra már azt hitte, joga van hozzá.
 */
export function visibleRoutes(session: Session): Route[] {
  return ROUTES.filter((route) => !route.permission || can(session, route.permission));
}

function currentRoute(session: Session): string {
  const hash = window.location.hash.replace(/^#\/?/, "");
  return visibleRoutes(session).some((r) => r.id === hash) ? hash : "attekintes";
}

export function App(): JSX.Element {
  const [session, setSession] = useState<Session | null>(loadSession());
  const [route, setRoute] = useState<string>(() => {
    const stored = loadSession();
    return stored ? currentRoute(stored) : "attekintes";
  });
  const [days, setDays] = useState(7);
  const [counts, setCounts] = useState<UnreadCount>({ unread: 0, errors: 0 });

  // Az olvasatlan értesítések száma a menüben. Percenként frissül: a
  // munkatárs nyitva hagyja a felületet, és látnia kell, ha hiba jött.
  const refreshCounts = useCallback(() => {
    if (!loadSession()) return;
    api
      .get<UnreadCount>("/admin/v1/notifications/count")
      .then(setCounts)
      .catch(() => {
        // A számláló nem kritikus — hiba esetén a régi érték marad.
      });
  }, []);

  useEffect(() => {
    if (!session) return;
    refreshCounts();
    const timer = window.setInterval(refreshCounts, 60_000);
    return () => window.clearInterval(timer);
  }, [session, refreshCounts]);

  useEffect(() => {
    const onHash = (): void => {
      const stored = loadSession();
      if (stored) setRoute(currentRoute(stored));
    };
    // A kliens ezt küldi, ha lejárt a munkamenet — így a felület azonnal
    // visszavisz a bejelentkezéshez, nem hibaüzenetet mutat minden nézetben.
    const onLogout = (): void => setSession(null);

    window.addEventListener("hashchange", onHash);
    window.addEventListener("kroki-logout", onLogout);
    return () => {
      window.removeEventListener("hashchange", onHash);
      window.removeEventListener("kroki-logout", onLogout);
    };
  }, []);

  if (!session) {
    return <Login onLogin={setSession} />;
  }

  const kilepes = (): void => {
    saveSession(null);
    setSession(null);
  };

  const routes = visibleRoutes(session);
  const active = routes.find((r) => r.id === route) ?? routes[0];

  return (
    <div className="app">
      <aside className="nav">
        <div className="nav__brand">
          <p className="eyebrow">Kroki AI</p>
          <strong>Adminisztráció</strong>
        </div>

        <nav>
          {routes.map((r) => (
            <a
              key={r.id}
              href={`#/${r.id}`}
              className={`nav__item${r.id === route ? " is-active" : ""}`}
            >
              <span className="nav__label">
                {r.label}
                {r.id === "ertesitesek" && counts.unread > 0 && (
                  <span
                    className={`badge${counts.errors > 0 ? " badge--bad" : ""}`}
                    aria-label={`${counts.unread} olvasatlan értesítés`}
                  >
                    {counts.unread}
                  </span>
                )}
              </span>
              <small>{r.hint}</small>
            </a>
          ))}
        </nav>

        <div className="nav__user">
          <strong>{session.display_name}</strong>
          <small>{session.role}</small>
          <button type="button" className="btn btn--ghost" onClick={kilepes}>
            Kilépés
          </button>
        </div>
      </aside>

      <main className="main">
        <header className="main__head">
          <div>
            <h1>{active?.label}</h1>
            <p className="muted">{active?.hint}</p>
          </div>

          {route === "attekintes" && (
            <div className="segmented">
              {[1, 7, 30].map((d) => (
                <button
                  key={d}
                  type="button"
                  className={days === d ? "is-active" : ""}
                  onClick={() => setDays(d)}
                >
                  {d === 1 ? "ma" : `${d} nap`}
                </button>
              ))}
            </div>
          )}
        </header>

        {route === "attekintes" && <OverviewView days={days} />}
        {route === "ertesitesek" && <NotificationsView onCountChange={setCounts} />}
        {route === "review" && <ReferralsView session={session} onlyReview />}
        {route === "beutalok" && <ReferralsView session={session} />}
        {route === "beszelgetesek" && can(session, "beszelgetest_olvas") && (
          <ConversationsView />
        )}
        {route === "kifejezesek" && <ReviewView session={session} />}
        {route === "szabalyok" && <RulesView session={session} />}
        {route === "tudasbazis" && <KnowledgeView session={session} />}
        {route === "beallitasok" && <SettingsView />}
        {route === "modellek" && can(session, "beallit") && <ModelsView session={session} />}
        {route === "felhasznalok" && can(session, "felhasznalot_kezel") && (
          <UsersView session={session} />
        )}
        {route === "fiokom" && <AccountView session={session} />}

        {!can(session, "olvas") && (
          <p className="notice notice--warn">
            A fiókjához nincs olvasási jog rendelve. Kérjen hozzáférést a
            rendszergazdától.
          </p>
        )}
      </main>
    </div>
  );
}
